import type OSS from 'ali-oss';
import { v4 as uuidv4 } from 'uuid';

import { TIMEOUT } from '@/utils/const';

import { MIN_PART_SIZE, PARALLEL } from '../../constants';
import { IStoreItem, ITokenData } from '../../type';
import { IInitResponse, IUploadOptions, IUploadRetryOptions, UploaderBase } from './base';

const fetchOSS = async () => {
  const OSSModule = await import('ali-oss');
  return OSSModule.default;
};

// OSS上传
export class OssUploader implements UploaderBase {
  constructor(private timeout: number = TIMEOUT) {}

  // 初始化
  public async init(tokenData: ITokenData): Promise<IInitResponse> {
    try {
      const ossOptions = this.getOssOptions(tokenData);
      const OSS = await fetchOSS();
      const client = new OSS(ossOptions);
      // const initRes = await client.initMultipartUpload(fileName);
      // if (!initRes) throw Error('Get Init Info Error');
      const uploadId = uuidv4();
      return {
        uploadId,
        client,
      };
    } catch (error) {
      console.error(error);
      return { uploadId: '', client: null };
    }
  }

  // 批量上传
  public async upload(params: IUploadOptions) {
    const { client, filePath, file, options } = params;
    return await (client as OSS).multipartUpload(filePath, file, {
      parallel: PARALLEL,
      partSize: MIN_PART_SIZE,
      headers: { 'cache-control': '', 'Content-Disposition': 'attachment' },
      ...options,
    });
  }

  // 取消上传
  public async cancel(params: IStoreItem) {
    const { client: _client, tokenData, progressData } = params;
    const { filePath } = tokenData;
    const { checkPoint, transCodeHandler } = progressData;
    transCodeHandler && transCodeHandler();

    if (!checkPoint) return true;
    let client = _client;
    if (!client) {
      const ossOptions = this.getOssOptions(tokenData);
      const OSS = await fetchOSS();
      client = new OSS(ossOptions);
    }

    const path = (checkPoint && checkPoint?.name) || filePath;
    const uploadId = checkPoint && checkPoint?.uploadId;
    const res: any = await (client as OSS).abortMultipartUpload(path, uploadId);
    if (String(res?.res?.status).startsWith('2')) return true;
    return false;
  }

  // 重试
  public async retry(params: IUploadRetryOptions) {
    const { info, options } = params;
    const { tokenData, client, file } = info;
    const { filePath } = tokenData;
    const multipartOptions = {
      parallel: PARALLEL,
      partSize: MIN_PART_SIZE,
      headers: { 'cache-control': '', 'Content-Disposition': 'attachment' },
      ...options,
    };
    if (client) {
      return await (client as OSS).multipartUpload(filePath, file, multipartOptions);
    } else {
      const OSS = await fetchOSS();
      const ossOptions = this.getOssOptions(tokenData);
      const client = new OSS(ossOptions);
      return await client.multipartUpload(filePath, file, multipartOptions);
    }
  }

  // 获取Oss Options
  private getOssOptions(tokenData: ITokenData) {
    const { uploadHost, bucket, region, accessKey, accessSecret, securityToken } =
      tokenData;
    const { host } = new URL(uploadHost);
    return {
      timeout: this.timeout || TIMEOUT,
      endpoint: `https://${bucket}.${host}`,
      region,
      accessKeyId: accessKey,
      accessKeySecret: accessSecret,
      stsToken: securityToken,
      bucket,
      cname: true,
      authorizationV4: true,
    };
  }
}
